/**
 * HomeTabs - Табы на главном экране (Web)
 * home, analytics, recurring, categories, limits
 * Pill-контейнер с полупрозрачным фоном и blur — как mobile UnifiedStyleHomeTabs
 */

import React, { useRef, useEffect } from 'react';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import { hapticFeedback } from '../../utils/telegram';
import './HomeTabs.css';

export type HomeTabType = 'home' | 'analytics' | 'recurring' | 'categories' | 'limits';

interface TabConfig {
  id: HomeTabType;
  icon: string;
  labelKey: string;
}

const tabConfigs: TabConfig[] = [
  { id: 'home', icon: 'home', labelKey: 'nav.home' },
  { id: 'analytics', icon: 'chart-bar', labelKey: 'operations.tabs.analytics' },
  { id: 'recurring', icon: 'sync', labelKey: 'operations.tabs.recurring' },
  { id: 'categories', icon: 'folder', labelKey: 'operations.tabs.categories' },
  { id: 'limits', icon: 'alert-circle', labelKey: 'operations.tabs.limits' },
];

interface HomeTabsProps {
  activeTab: HomeTabType;
  onTabChange: (tab: HomeTabType) => void;
}

export function HomeTabs({ activeTab, onTabChange }: HomeTabsProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleTabPress = (tabId: HomeTabType) => {
    if (tabId !== activeTab) {
      hapticFeedback.selection();
      onTabChange(tabId);
    }
  };

  useEffect(() => {
    const activeIndex = tabConfigs.findIndex((tab) => tab.id === activeTab);
    if (activeIndex > 0 && scrollRef.current) {
      const tabWidth = 90;
      const scrollTo = Math.max(0, activeIndex * tabWidth - 40);
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    } else if (activeIndex === 0 && scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  return (
    <div className="home-tabs">
      <div className="home-tabs__glass">
        <div
          ref={scrollRef}
          className="home-tabs__scroll"
          role="tablist"
        >
          {tabConfigs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`home-tabs__tab ${isActive ? 'home-tabs__tab--active' : ''}`}
                onClick={() => handleTabPress(tab.id)}
              >
                <Icon
                  name={tab.icon}
                  size={18}
                  color={isActive ? 'var(--color-primary)' : 'var(--color-text-primary)'}
                />
                <span className="home-tabs__label">{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { tabConfigs as homeTabConfigs };
